
import {
  defineComponent,
  ref,
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from 'vue';
import RadioButton from 'primevue/radiobutton';
import OverlayPanel from 'primevue/overlaypanel';
import { useI18n } from 'vue-i18n';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { UserState } from '@/composables/User';
import SegmentationsPanel from '@/components/segmentations/SegmentationsPanel.vue';
import type { StandardQueryListing, SpmApiFilter } from '@/components/table-rest-api/types';

const BATCH_SIZE = 20;
const DEBOUNCE_MS = 300;

interface ContactRow {
  id_contact: number | string;
  first_name?: string;
  last_name?: string;
  email?: string;
  matched_conditions?: number[];
}

interface SegmentCondition {
  id: number;
  config: any;
}

interface SegmentInfo {
  hasSegments: boolean;
  conditions: SegmentCondition[];
  matchingCount: number;
}

export default defineComponent({
  name: 'ContactListScroll',

  components: {
    RadioButton,
    OverlayPanel,
    SegmentationsPanel,
  },

  props: {
    modelValue: {
      type: Number as any,
      default: undefined,
    },
    templateId: {
      type: Number,
      default: undefined,
    },
    initialContactInfo: {
      type: Object as any,
      default: null,
    },
    rawHtml: {
      type: String,
      default: undefined,
    },
    allContactsOnly: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update:modelValue', 'update:contact-info', 'update:segment-info'],

  setup(props, { emit }) {
    const allContactsOnly = computed(() => props.allContactsOnly);

    const { t } = useI18n();

    // ===== Segment state =====
    const segmentInfo = reactive<SegmentInfo>({
      hasSegments: false,
      conditions: [],
      matchingCount: 0,
    });

    // ===== Selected conditions state (all selected by default) =====
    const selectedConditionIds = ref<number[]>([]);

    // ===== Popover state =====
    const activePopover = ref<SegmentCondition | null>(null);
    const conditionOverlayRef = ref();

    // ===== Matching contacts state =====
    const matchingContacts = ref<ContactRow[]>([]);
    const matchingOffset = ref(0);
    const matchingHasMore = ref(true);
    const matchingTotal = ref(0);

    // ===== Non-matching contacts state =====
    const nonMatchingContacts = ref<ContactRow[]>([]);
    const nonMatchingOffset = ref(0);
    const nonMatchingHasMore = ref(true);
    const nonMatchingTotal = ref(0);

    // ===== Regular (no-segment) contacts state =====
    const contacts = ref<ContactRow[]>([]);
    const offset = ref(0);
    const hasMore = ref(true);

    // ===== Tab filter state =====
    const activeTab = ref<'matching' | 'others'>('matching');

    // ===== Shared state =====
    const initialized = ref(false);
    const loading = ref(false);
    const searchTerm = ref('');
    const scrollContainerRef = ref<HTMLElement | null>(null);
    const sentinelRef = ref<HTMLElement | null>(null);
    let observer: IntersectionObserver | null = null;
    let debounceTimer: ReturnType<typeof setTimeout> | null = null;

    const isInitialLoading = computed(() => {
      // Show skeleton until initialized or while loading with no data
      if (!initialized.value) return true;
      if (segmentInfo.hasSegments && !allContactsOnly.value) {
        return loading.value && matchingContacts.value.length === 0 && nonMatchingContacts.value.length === 0;
      }
      return loading.value && contacts.value.length === 0;
    });

    const isEmpty = computed(() => {
      if (segmentInfo.hasSegments && !allContactsOnly.value) {
        if (activeTab.value === 'matching') return matchingContacts.value.length === 0;
        return nonMatchingContacts.value.length === 0;
      }
      return contacts.value.length === 0;
    });

    // ===== Helpers =====
    const cleanStr = (str: string | null | undefined): string => {
      if (!str || str === 'null' || str === 'undefined') return '';
      return str;
    };

    const formatFullName = (firstName?: string, lastName?: string): string => {
      const first = cleanStr(firstName);
      const last = cleanStr(lastName);
      if (!first && !last) return '';
      return `${first} ${last}`.trim();
    };

    // ===== Segment info fetch =====
    const fetchSegmentInfo = async () => {
      if (!props.templateId) {
        emit('update:segment-info', {
          hasSegments: false,
          conditions: [],
          matchingCount: 0,
        });
        return;
      }
      const idShop = UserState.activeShop?.id ?? 0;
      if (!idShop) {
        emit('update:segment-info', {
          hasSegments: false,
          conditions: [],
          matchingCount: 0,
        });
        return;
      }

      try {
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/template/segment-info',
          {},
          '',
          { idShop, idTemplate: props.templateId, rawHtml: props.rawHtml || undefined },
          true,
        );

        if (result?.success) {
          segmentInfo.hasSegments = result.hasSegments;
          segmentInfo.conditions = result.conditions || [];
          segmentInfo.matchingCount = result.matchingCount;
          // Select all conditions by default
          selectedConditionIds.value = segmentInfo.conditions.map((c: SegmentCondition) => c.id);
          emit('update:segment-info', {
            hasSegments: segmentInfo.hasSegments,
            conditions: segmentInfo.conditions,
            matchingCount: segmentInfo.matchingCount,
          });
        }
      } catch (err) {
        console.error('ContactListScroll: segment-info error', err);
        emit('update:segment-info', {
          hasSegments: false,
          conditions: [],
          matchingCount: 0,
        });
      }
    };

    // ===== Segment contacts fetch =====
    const fetchSegmentContacts = async (mode: 'matching' | 'non_matching', reset = false) => {
      if (loading.value) return;
      const isMatching = mode === 'matching';
      const currentHasMore = isMatching ? matchingHasMore : nonMatchingHasMore;
      if (!reset && !currentHasMore.value) return;

      loading.value = true;

      if (reset) {
        if (isMatching) {
          matchingOffset.value = 0;
          matchingHasMore.value = true;
        } else {
          nonMatchingOffset.value = 0;
          nonMatchingHasMore.value = true;
        }
      }

      const idShop = UserState.activeShop?.id ?? 0;
      const currentOffset = isMatching ? matchingOffset.value : nonMatchingOffset.value;

      try {
        const result = await nestCall(
          'post',
          NestApiType.DEFAULT,
          '/template/contacts-by-segment',
          {},
          '',
          {
            idShop,
            idTemplate: props.templateId,
            mode,
            offset: currentOffset,
            limit: BATCH_SIZE,
            search: searchTerm.value.trim() || undefined,
            selectedConditionIds: selectedConditionIds.value,
            rawHtml: props.rawHtml || undefined,
          },
          true,
        );

        if (result?.success && result.data) {
          const newContacts: ContactRow[] = result.data;
          const total = result.total ?? 0;

          if (isMatching) {
            matchingContacts.value = reset ? newContacts : [...matchingContacts.value, ...newContacts];
            matchingTotal.value = total;
            matchingOffset.value = matchingContacts.value.length;
            matchingHasMore.value = matchingContacts.value.length < total;
          } else {
            nonMatchingContacts.value = reset ? newContacts : [...nonMatchingContacts.value, ...newContacts];
            nonMatchingTotal.value = total;
            nonMatchingOffset.value = nonMatchingContacts.value.length;
            nonMatchingHasMore.value = nonMatchingContacts.value.length < total;
          }
        } else {
          if (reset) {
            if (isMatching) matchingContacts.value = [];
            else nonMatchingContacts.value = [];
          }
          if (isMatching) matchingHasMore.value = false;
          else nonMatchingHasMore.value = false;
        }
      } catch (error) {
        console.error(`ContactListScroll: fetch ${mode} error`, error);
        if (reset) {
          if (isMatching) matchingContacts.value = [];
          else nonMatchingContacts.value = [];
        }
        if (isMatching) matchingHasMore.value = false;
        else nonMatchingHasMore.value = false;
      } finally {
        loading.value = false;
      }
    };

    // ===== Regular contacts fetch (no segments) =====
    const buildOrFilters = (): SpmApiFilter[] => {
      const term = searchTerm.value.trim();
      if (!term) return [];

      return [
        { field: 'first_name', operator: 'CONTAINS', values: term },
        { field: 'last_name', operator: 'CONTAINS', values: term },
        { field: 'email', operator: 'CONTAINS', values: term },
      ];
    };

    const fetchRegularContacts = async (reset = false) => {
      if (loading.value) return;
      if (!reset && !hasMore.value) return;

      loading.value = true;

      if (reset) {
        offset.value = 0;
        hasMore.value = true;
      }

      const query: StandardQueryListing = {
        offset: offset.value,
        limit: BATCH_SIZE,
        fields: 'id_contact,first_name,last_name,email',
      };

      const orFilters = buildOrFilters();
      if (orFilters.length > 0) {
        query.orFilters = orFilters;
      }

      try {
        const response = await nestCall(
          'get',
          NestApiType.CLIENT_API,
          '/contacts/all',
          {},
          '',
          undefined,
          true,
          query,
        );

        if (response?.data) {
          const newContacts: ContactRow[] = response.data;
          contacts.value = reset ? newContacts : [...contacts.value, ...newContacts];
          const total = response.meta?.pagination?.total_items ?? 0;
          hasMore.value = contacts.value.length < total;
          offset.value = contacts.value.length;
        } else {
          if (reset) contacts.value = [];
          hasMore.value = false;
        }
      } catch (error) {
        console.error('ContactListScroll: fetch error', error);
        if (reset) contacts.value = [];
        hasMore.value = false;
      } finally {
        loading.value = false;
      }
    };

    // ===== Tab switch =====
    const switchTab = async (tab: 'matching' | 'others') => {
      if (activeTab.value === tab) return;
      activeTab.value = tab;

      // Load data for the tab if not yet loaded
      if (tab === 'others' && nonMatchingContacts.value.length === 0 && nonMatchingHasMore.value) {
        await fetchSegmentContacts('non_matching', true);
      }
    };

    // ===== Unified fetch dispatcher =====
    const fetchNext = async () => {
      if (!initialized.value) return;
      if (segmentInfo.hasSegments && !allContactsOnly.value) {
        if (activeTab.value === 'matching' && matchingHasMore.value) {
          await fetchSegmentContacts('matching');
        } else if (activeTab.value === 'others' && nonMatchingHasMore.value) {
          await fetchSegmentContacts('non_matching');
        }
      } else {
        await fetchRegularContacts();
      }
    };

    const resetAndFetch = async () => {
      // Reset loading state to allow new fetches
      loading.value = false;

      if (segmentInfo.hasSegments && !allContactsOnly.value) {
        matchingContacts.value = [];
        nonMatchingContacts.value = [];
        matchingOffset.value = 0;
        nonMatchingOffset.value = 0;
        matchingHasMore.value = true;
        nonMatchingHasMore.value = true;
        matchingTotal.value = 0;
        nonMatchingTotal.value = 0;

        // Load both tabs so counts are available immediately
        await fetchSegmentContacts('matching', true);
        await fetchSegmentContacts('non_matching', true);
      } else {
        contacts.value = [];
        await fetchRegularContacts(true);
      }
    };

    // ===== Search =====
    const onSearchInput = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        resetAndFetch();
      }, DEBOUNCE_MS);
    };

    const clearSearch = () => {
      searchTerm.value = '';
      resetAndFetch();
    };

    // ===== Selection =====
    const selectedContactInfo = ref<{ name: string; email: string } | null>(props.initialContactInfo || null);

    // Sync when parent updates initialContactInfo after mount (e.g. restored from localStorage)
    watch(() => props.initialContactInfo, (newVal) => {
      if (newVal) {
        selectedContactInfo.value = newVal;
      }
    });

    const selectContact = (contact: ContactRow) => {
      const id = Number(contact.id_contact);
      const info = {
        name: formatFullName(contact.first_name, contact.last_name),
        email: contact.email || '',
      };
      selectedContactInfo.value = info;
      emit('update:contact-info', info);
      emit('update:modelValue', id);
    };

    const deselectContact = () => {
      selectedContactInfo.value = null;
      emit('update:contact-info', null);
      emit('update:modelValue', undefined);
    };

    // ===== Condition toggle =====
    const toggleCondition = (conditionId: number) => {
      const index = selectedConditionIds.value.indexOf(conditionId);
      if (index === -1) {
        selectedConditionIds.value = [...selectedConditionIds.value, conditionId];
      } else {
        selectedConditionIds.value = selectedConditionIds.value.filter(id => id !== conditionId);
      }
      // Refetch contacts with new selection
      resetAndFetch();
    };

    // ===== Popover functions =====
    let popoverHideTimeout: ReturnType<typeof setTimeout> | null = null;

    const showConditionPopover = (event: Event, condition: SegmentCondition) => {
      // Clear any pending hide timeout
      if (popoverHideTimeout) {
        clearTimeout(popoverHideTimeout);
        popoverHideTimeout = null;
      }
      activePopover.value = condition;
      conditionOverlayRef.value?.show(event);
    };

    const hideConditionPopover = () => {
      // Small delay to allow moving mouse to the popover
      popoverHideTimeout = setTimeout(() => {
        conditionOverlayRef.value?.hide();
        activePopover.value = null;
      }, 150);
    };

    const toggleConditionPopover = (event: Event, condition: SegmentCondition) => {
      activePopover.value = condition;
      conditionOverlayRef.value?.toggle(event);
    };

    // ===== Observer =====
    const setupObserver = () => {
      if (!sentinelRef.value) return;

      observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry?.isIntersecting && !loading.value) {
            fetchNext();
          }
        },
        {
          root: scrollContainerRef.value,
          rootMargin: '100px',
          threshold: 0,
        },
      );

      observer.observe(sentinelRef.value);
    };

    // ===== Lifecycle =====
    onMounted(async () => {
      if (props.templateId) {
        await fetchSegmentInfo();
      }
      await resetAndFetch();
      initialized.value = true;
      nextTick(() => setupObserver());
    });

    onBeforeUnmount(() => {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    });

    watch(sentinelRef, () => {
      if (observer) observer.disconnect();
      nextTick(() => setupObserver());
    });

    return {
      t,
      segmentInfo,
      selectedConditionIds,
      activePopover,
      conditionOverlayRef,
      matchingContacts,
      nonMatchingContacts,
      matchingHasMore,
      matchingTotal,
      nonMatchingTotal,
      contacts,
      loading,
      initialized,
      isInitialLoading,
      isEmpty,
      searchTerm,
      scrollContainerRef,
      sentinelRef,
      allContactsOnly,
      activeTab,
      formatFullName,
      onSearchInput,
      clearSearch,
      selectedContactInfo,
      selectContact,
      deselectContact,
      toggleCondition,
      showConditionPopover,
      hideConditionPopover,
      toggleConditionPopover,
      switchTab,
    };
  },
});
